import { createContext, Dispatch, useContext, useReducer } from 'react';
import { HearingResultData } from '../types';

interface TestState {
  contactid: string;
  facilityid: string | null;
  patientid: string;
  patientfirstname: string | null;
  patientlastname: string | null;
  patientsex: string | null;
  patientdob: string | null;
  patientage: string | null;
  patientexternalid: string | null;
  adminname: string | null;
  adminemail: string | null;
  email: string;
  resultsurl: string;
  overallscore: number | null;
  nameonresult: string;
  hearingresultid: string | null;
  hearingresultdata: HearingResultData | null;
  vptaoverall: number | null;
  vptaleft: number | null;
  vptaright: number | null;
  endoftestnotes: string;
  notesabouthearingloss: string;
  dob: string;
  createdAtState: string | null;
  currentStep: number;
  progress: number;
  nextEarCalled: boolean;
  finishTrainingCalled: boolean;
  /** True only after both ears pass the headphone placement check. */
  headphonesCheckPassed: boolean;
}

type Action =
  | {
      type: 'UPDATE_CONTACTID';
      payload: string;
    }
  | {
      type: 'UPDATE_FACILITYID';
      payload: string;
    }
  | {
      type: 'UPDATE_PATIENTID';
      payload: string;
    }
  | {
      type: 'UPDATE_EARHISTORY';
      payload: {
        contactid: string;
        facilityid: string;
        patientfirstname: string;
        patientlastname: string;
        patientsex: string;
        patientdob: string;
        patientage: string;
        patientexternalid: string;
        nameonresult: string;
        adminname: string;
        adminemail: string;
      };
    }
  | {
      type: 'UPDATE_ENDOFTESTNOTES';
      payload: string;
    }
  | {
      type: 'UPDATE_DEMOGRAPHICS';
      payload: {
        contactid: string;
        patientid: string;
        email: string;
        nameonresult: string;
        dob: string;
        createdAtState?: string;
      };
    }
  | {
      type: 'UPDATE_RESULTSURL';
      payload: string;
    }
  | {
      type: 'UPDATE_OVERALLSCORE';
      payload: number;
    }
  | {
      type: 'UPDATE_HEARINGRESULTID';
      payload: string;
    }
  | {
      type: 'UPDATE_HEARINGRESULTDATA';
      payload: HearingResultData;
    }
  | {
      type: 'UPDATE_VPTAOVERALL';
      payload: number;
    }
  | {
      type: 'UPDATE_VPTALEFT';
      payload: number;
    }
  | {
      type: 'UPDATE_VPTARIGHT';
      payload: number;
    }
  | {
      type: 'UPDATE_NOTESABOUTHEARINGLOSS';
      payload: string;
    }
  | {
      type: 'UPDATE_FACILITYID';
      payload: string;
    }
  | {
      type: 'UPDATE_CURRENT_STEP';
      payload: number;
    }
  | {
      type: 'UPDATE_PROGRESS';
      payload: number;
    }
  | {
      type: 'UPDATE_NEXT_EAR_CALLED';
      payload: boolean;
    }
  | {
      type: 'UPDATE_FINISH_TRAINING_CALLED';
      payload: boolean;
    }
  | {
      type: 'UPDATE_CREATED_AT_STATE';
      payload: string;
    }
  | {
      type: 'UPDATE_HEADPHONES_CHECK_PASSED';
      payload: boolean;
    };

const initialState: TestState = {
  contactid: '',
  facilityid: '',
  patientid: '',
  patientfirstname: '',
  patientlastname: '',
  patientsex: '',
  patientdob: '',
  patientage: '',
  adminname: '',
  adminemail: '',
  patientexternalid: '',
  email: '',
  resultsurl: '',
  overallscore: null,
  nameonresult: '',
  hearingresultid: null,
  hearingresultdata: null,
  vptaoverall: null,
  vptaleft: null,
  vptaright: null,
  notesabouthearingloss: '',
  endoftestnotes: '',
  dob: '',
  createdAtState: null,
  currentStep: 0,
  progress: 0,
  nextEarCalled: false,
  finishTrainingCalled: false,
  headphonesCheckPassed: false,
};

const TestContext = createContext<{
  state: TestState;
  dispatch: Dispatch<Action>;
}>({ state: initialState, dispatch: () => {} });

const reducer = (state: TestState, action: Action) => {
  switch (action.type) {
    case 'UPDATE_CONTACTID':
      return { ...state, contactid: action.payload };
    case 'UPDATE_FACILITYID':
      return { ...state, facilityid: action.payload };
    case 'UPDATE_PATIENTID':
      return { ...state, patientid: action.payload };
    case 'UPDATE_EARHISTORY':
      return { ...state, ...action.payload };
    case 'UPDATE_DEMOGRAPHICS':
      return { ...state, ...action.payload, createdAtState: action.payload.createdAtState || null };
    case 'UPDATE_RESULTSURL':
      return { ...state, resultsurl: action.payload };
    case 'UPDATE_OVERALLSCORE':
      return { ...state, overallscore: action.payload };
    case 'UPDATE_HEARINGRESULTID':
      return { ...state, hearingresultid: action.payload };
    case 'UPDATE_HEARINGRESULTDATA':
      return { ...state, hearingresultdata: action.payload };
    case 'UPDATE_VPTAOVERALL':
      return { ...state, vptaoverall: action.payload };
    case 'UPDATE_VPTALEFT':
      return { ...state, vptaleft: action.payload };
    case 'UPDATE_VPTARIGHT':
      return { ...state, vptaright: action.payload };
    case 'UPDATE_NOTESABOUTHEARINGLOSS':
      return { ...state, notesabouthearingloss: action.payload };
    case 'UPDATE_ENDOFTESTNOTES':
      return { ...state, endoftestnotes: action.payload };
    case 'UPDATE_CURRENT_STEP':
      return { ...state, currentStep: action.payload };
    case 'UPDATE_PROGRESS':
      return { ...state, progress: action.payload };
    case 'UPDATE_NEXT_EAR_CALLED':
      return { ...state, nextEarCalled: action.payload };
    case 'UPDATE_FINISH_TRAINING_CALLED':
      return { ...state, finishTrainingCalled: action.payload };
    case 'UPDATE_CREATED_AT_STATE':
      return { ...state, createdAtState: action.payload };
    case 'UPDATE_HEADPHONES_CHECK_PASSED':
      return { ...state, headphonesCheckPassed: action.payload };
    default:
      return state;
  }
};

export const TestProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  return (
    <TestContext.Provider value={{ state, dispatch }}>
      {children}
    </TestContext.Provider>
  );
};

export const useTestState = () => {
  return useContext(TestContext);
};
